import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Calendar, CheckCircle2, Circle, Clock, FileText, Image as ImageIcon, Mic, Video, ExternalLink, Send, MessageSquare, FileSignature, ShieldCheck } from "lucide-react";
import { buscarProjetoCliente, AnexoCliente, listarDocumentosClienteProjeto } from "../../services/clientePortal";
import { anexoUrlAbsoluto } from "../../services/tarefas";
import { clientePostarComentario, clienteCriarDemanda } from "../../services/comentarios";
import { AudioWavePlayer } from "../../components/AudioWavePlayer";

const STATUS_LABELS: Record<string, string> = {
  planejamento: "Em planejamento",
  ativo: "Em andamento",
  pausado: "Pausado",
  entregue: "Entregue",
};

export function ClienteProjetoDetalhe() {
  const { id } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const [novoComentario, setNovoComentario] = useState("");
  const { data: p, isLoading } = useQuery({ queryKey: ["cliente-projeto", id], queryFn: () => buscarProjetoCliente(id!), enabled: !!id });
  const { data: documentos = [] } = useQuery({
    queryKey: ["cliente-documentos", id],
    queryFn: () => listarDocumentosClienteProjeto(id!),
    enabled: !!id,
  });

  const comentarM = useMutation({
    mutationFn: () => clientePostarComentario(id!, novoComentario),
    onSuccess: () => {
      setNovoComentario("");
      qc.invalidateQueries({ queryKey: ["cliente-projeto", id] });
    },
  });

  // Briefing/nova demanda (Relatórios #7).
  const [demandaAberta, setDemandaAberta] = useState(false);
  const [demTitulo, setDemTitulo] = useState("");
  const [demDescricao, setDemDescricao] = useState("");
  const [demPrazo, setDemPrazo] = useState("");
  const demandaM = useMutation({
    mutationFn: () => clienteCriarDemanda(id!, { titulo: demTitulo, descricao: demDescricao || undefined, prazoDesejado: demPrazo || undefined }),
    onSuccess: () => {
      setDemTitulo(""); setDemDescricao(""); setDemPrazo(""); setDemandaAberta(false);
      qc.invalidateQueries({ queryKey: ["cliente-projeto", id] });
    },
    onError: (e: any) => alert(e?.response?.data?.error?.message || "Não foi possível enviar a solicitação."),
  });

  if (isLoading) return <p className="text-sm text-slate-400">Carregando…</p>;
  if (!p) return <p className="text-sm text-slate-500">Projeto não encontrado.</p>;

  const totalMarcos = p.marcos.length;
  const concluidosMarcos = p.marcos.filter(m => m.status === "concluido").length;
  const totalTarefas = p.tarefas.length;
  const concluidasTarefas = p.tarefas.filter(t => t.status === "concluido").length;
  const progresso = totalMarcos > 0 ? Math.round((concluidosMarcos / totalMarcos) * 100) : (totalTarefas > 0 ? Math.round((concluidasTarefas / totalTarefas) * 100) : 0);

  return (
    <div className="space-y-6">
      <Link to="/cliente" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700">
        <ArrowLeft size={14} /> Voltar
      </Link>

      <header className="bg-white border border-slate-200 rounded-xl p-5">
        <h1 className="text-xl font-bold text-slate-800">{p.nome}</h1>
        {p.resumoPublico && <p className="text-sm text-slate-600 mt-2 whitespace-pre-wrap">{p.resumoPublico}</p>}
        <div className="flex flex-wrap gap-4 mt-3 text-xs text-slate-500">
          <span>Status: <strong className="text-slate-700">{STATUS_LABELS[p.status] || p.status}</strong></span>
          <span className="flex items-center gap-1"><Calendar size={11} /> Início: {new Date(p.dataInicio).toLocaleDateString("pt-BR")}</span>
          <span className="flex items-center gap-1"><Calendar size={11} /> Entrega prevista: {new Date(p.prazoEntrega).toLocaleDateString("pt-BR")}</span>
        </div>
        <div className="mt-4">
          <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
            <div className="h-full bg-blue-500 transition-all" style={{ width: `${progresso}%` }} />
          </div>
          <p className="text-xs text-slate-500 mt-1">Progresso: {progresso}%</p>
        </div>
      </header>

      {/* Marcos */}
      {p.marcos.length > 0 && (
        <section>
          <h2 className="text-sm font-semibold text-slate-600 uppercase mb-2">Marcos</h2>
          <div className="space-y-2">
            {p.marcos.map(m => (
              <div key={m.id} className="bg-white border border-slate-200 rounded-lg p-3 flex items-start gap-3">
                {m.status === "concluido" ? (
                  <CheckCircle2 size={18} className="text-emerald-500 mt-0.5 shrink-0" />
                ) : m.status === "atrasado" ? (
                  <Clock size={18} className="text-red-500 mt-0.5 shrink-0" />
                ) : (
                  <Circle size={18} className="text-slate-300 mt-0.5 shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-slate-800">{m.nome}</p>
                  {m.descricao && <p className="text-xs text-slate-500 mt-0.5">{m.descricao}</p>}
                  <p className="text-[11px] text-slate-400 mt-1">
                    Previsto: {new Date(m.dataPrevista).toLocaleDateString("pt-BR")}
                    {m.dataRealizada && ` · Concluído em: ${new Date(m.dataRealizada).toLocaleDateString("pt-BR")}`}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Tarefas visíveis */}
      {p.tarefas.length > 0 && (
        <section>
          <h2 className="text-sm font-semibold text-slate-600 uppercase mb-2">Andamento</h2>
          <div className="space-y-2">
            {p.tarefas.map(t => (
              <div key={t.id} className="bg-white border border-slate-200 rounded-lg p-3 flex items-center gap-3">
                {t.status === "concluido" ? (
                  <CheckCircle2 size={16} className="text-emerald-500 shrink-0" />
                ) : (
                  <Circle size={16} className="text-slate-300 shrink-0" />
                )}
                <span className={`flex-1 text-sm ${t.status === "concluido" ? "text-slate-500 line-through" : "text-slate-800"}`}>{t.titulo}</span>
                <span className="text-[11px] text-slate-400">prazo: {new Date(t.prazo).toLocaleDateString("pt-BR")}</span>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Documentos para assinatura */}
      {documentos.length > 0 && (
        <section>
          <h2 className="text-sm font-semibold text-slate-600 uppercase mb-2 flex items-center gap-2">
            <FileSignature size={14} /> Documentos
          </h2>
          <div className="space-y-2">
            {documentos.map((d) => {
              const tokenSeu = d.signatarios.find((s) => s.tokenAcesso)?.tokenAcesso || null;
              const minhaAssinatura = d.signatarios.find((s) => s.tokenAcesso);
              const url = anexoUrlAbsoluto(d.arquivoFinalUrl || d.arquivoUrl);
              return (
                <div key={d.id} className="bg-white border border-slate-200 rounded-lg p-3">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-800">{d.titulo}</p>
                      <p className="text-[11px] text-slate-500 font-mono">Código: {d.codigoVerificacao}</p>
                    </div>
                    <span className={`text-[10px] uppercase font-bold px-1.5 py-0.5 rounded ${d.status === "assinado" ? "bg-emerald-100 text-emerald-700" : d.status === "aguardando_assinatura" ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-600"}`}>
                      {d.status === "aguardando_assinatura" ? "aguardando" : d.status}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 mt-2 flex-wrap">
                    {tokenSeu && minhaAssinatura?.status !== "assinado" && (
                      <Link to={`/assinar/${tokenSeu}`} className="text-xs flex items-center gap-1 bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded">
                        <FileSignature size={12} /> Assinar agora
                      </Link>
                    )}
                    <a href={url} target="_blank" rel="noreferrer" className="text-xs flex items-center gap-1 bg-slate-100 hover:bg-slate-200 text-slate-700 px-3 py-1.5 rounded">
                      <ExternalLink size={12} /> Ver PDF
                    </a>
                    <a href={`/verificar/${d.codigoVerificacao}`} target="_blank" rel="noreferrer" className="text-xs flex items-center gap-1 text-blue-600 hover:text-blue-700 ml-auto">
                      <ShieldCheck size={12} /> Verificar autenticidade
                    </a>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Anexos */}
      {p.anexos.length > 0 && (
        <section>
          <h2 className="text-sm font-semibold text-slate-600 uppercase mb-2">Arquivos</h2>
          <div className="grid gap-2">
            {p.anexos.map(a => <AnexoCard key={a.id} a={a} />)}
          </div>
        </section>
      )}

      {/* Solicitar nova demanda / briefing (Relatórios #7) */}
      <section>
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-sm font-semibold text-slate-600 uppercase flex items-center gap-2">
            <FileText size={14} /> Solicitar nova demanda
          </h2>
          {!demandaAberta && (
            <button onClick={() => setDemandaAberta(true)} className="text-sm bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded">
              + Nova solicitação
            </button>
          )}
        </div>
        {demandaAberta && (
          <div className="bg-white border border-slate-200 rounded-lg p-3 space-y-2">
            <input
              value={demTitulo}
              onChange={(e) => setDemTitulo(e.target.value)}
              placeholder="Título da demanda (ex.: Novo vídeo institucional)"
              className="w-full px-3 py-2 rounded border border-slate-200 outline-none text-sm focus:border-blue-500"
            />
            <textarea
              value={demDescricao}
              onChange={(e) => setDemDescricao(e.target.value)}
              rows={4}
              placeholder="Descreva o briefing: objetivo, referências, formato, mensagem principal, prazo desejado…"
              className="w-full px-3 py-2 rounded border border-slate-200 outline-none text-sm focus:border-blue-500"
            />
            <div className="flex items-center gap-2 flex-wrap">
              <label className="text-xs text-slate-500">Prazo desejado</label>
              <input type="date" value={demPrazo} onChange={(e) => setDemPrazo(e.target.value)} className="px-2 py-1 rounded border border-slate-200 text-sm outline-none focus:border-blue-500" />
              <div className="ml-auto flex items-center gap-2">
                <button onClick={() => setDemandaAberta(false)} className="text-sm text-slate-500 px-3 py-1.5 rounded hover:bg-slate-100">Cancelar</button>
                <button
                  onClick={() => demandaM.mutate()}
                  disabled={!demTitulo.trim() || demandaM.isPending}
                  className="text-sm bg-blue-600 hover:bg-blue-700 text-white px-4 py-1.5 rounded flex items-center gap-1.5 disabled:opacity-50"
                >
                  <Send size={14} /> Enviar solicitação
                </button>
              </div>
            </div>
            <p className="text-[11px] text-slate-400">A solicitação entra no quadro do time como uma demanda e fica visível pra você acompanhar.</p>
          </div>
        )}
      </section>

      {/* Conversa */}
      <section>
        <h2 className="text-sm font-semibold text-slate-600 uppercase mb-2 flex items-center gap-2">
          <MessageSquare size={14} /> Conversa
        </h2>
        <div className="space-y-2 mb-3">
          {p.comentarios.length === 0 && (
            <p className="text-sm text-slate-400 py-2">Nenhuma mensagem ainda — comece você!</p>
          )}
          {p.comentarios.map((c) => (
            <div
              key={c.id}
              className={`rounded-lg p-3 border ${c.autor.perfil === "cliente" ? "border-blue-200 bg-blue-50/50" : "border-slate-200 bg-white"}`}
            >
              <div className="flex items-center gap-2 text-xs">
                <strong className="text-slate-700">{c.autor.nome}</strong>
                <span className="ml-auto text-slate-400">{new Date(c.criadoEm).toLocaleString("pt-BR")}</span>
              </div>
              <p className="text-sm text-slate-700 mt-1 whitespace-pre-wrap">{c.texto}</p>
            </div>
          ))}
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-3">
          <textarea
            value={novoComentario}
            onChange={(e) => setNovoComentario(e.target.value)}
            rows={2}
            placeholder="Escreva uma mensagem ao time…"
            className="w-full px-3 py-2 rounded border border-slate-200 outline-none text-sm focus:border-blue-500"
          />
          <div className="flex justify-end mt-2">
            <button
              onClick={() => comentarM.mutate()}
              disabled={!novoComentario.trim() || comentarM.isPending}
              className="text-sm bg-blue-600 hover:bg-blue-700 text-white px-4 py-1.5 rounded flex items-center gap-1.5 disabled:opacity-50"
            >
              <Send size={14} /> Enviar
            </button>
          </div>
        </div>
      </section>

      {p.marcos.length === 0 && p.tarefas.length === 0 && p.anexos.length === 0 && p.comentarios.length === 0 && (
        <p className="text-sm text-slate-400 text-center py-8">Ainda não há atualizações para mostrar. Volte em breve!</p>
      )}
    </div>
  );
}

function AnexoCard({ a }: { a: AnexoCliente }) {
  const url = anexoUrlAbsoluto(a.url);
  const Icon = a.tipo === "imagem" ? ImageIcon : a.tipo === "audio" ? Mic : a.tipo === "video" ? Video : FileText;
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-3">
      <div className="flex items-center gap-2 text-sm">
        <Icon size={14} className="text-slate-400" />
        <span className="flex-1 truncate text-slate-700">{a.nomeOriginal}</span>
        <a href={url} target="_blank" rel="noreferrer" className="text-blue-600 hover:text-blue-700 text-xs flex items-center gap-1">
          <ExternalLink size={12} /> Abrir
        </a>
      </div>
      {a.descricao && <p className="text-xs text-slate-500 mt-1">{a.descricao}</p>}
      {a.tipo === "imagem" && <img src={url} alt={a.nomeOriginal} className="mt-2 max-h-48 object-contain rounded border border-slate-100 bg-slate-50 w-full" />}
      {a.tipo === "audio" && <div className="mt-2"><AudioWavePlayer url={url} variante="claro" /></div>}
      {a.tipo === "video" && <video controls src={url} className="w-full max-h-64 mt-2" />}
    </div>
  );
}
